<script>
  import { onMount, onDestroy } from "svelte";
  import Scroller from "@sveltejs/svelte-scroller";
  import anime from "animejs";
  import debounce from "lodash.debounce";
  import normalizeWheel from "normalize-wheel";
  import Logo from "./components/Logo.svelte";
  import LineTLBR1 from "./components/LineTLBR1.svelte";
  import LineBLTR1 from "./components/LineBLTR1.svelte";
  import LineTRBL1 from "./components/LineTRBL1.svelte";
  import StaticLine from "./components/StaticLine.svelte";

  import typeform from '@typeform/embed'
  import '@typeform/embed/build/css/popup.css'

  let theYear = new Date().getFullYear();

  let index = 0;
  let offset1 = 0;
  let progress;
  let tweenAmt = 0;
  let tweenAnimation;
  let isBelowTween = false;
  let navHidden = false;
  const maxTweenAmt = 3000;


  let innerWidth;
  let innerHeight;
  let scrollY;

  let topBlock;
  let midBlock;
  let botBlock;
  let heroBlock;
  let section1_1Block;
  let section1_2Block;
  let section1_3Block;
  let section2_1Block;
  let section2_2Block;
  let section2_3Block;
  let section3_1Block;
  let section3_2Block;
  let section3_3Block;
  let section4_1Block;
  let section4_2Block;
  let section4_3Block;
  let tweenBlock;
  let tweenBlockCenter;
  let tween0Block;
  let tween1Block;
  let tween2Block;
  let tween3Block;
  let tween4Block;
  let tween5Block;
  let tween6Block;
  let investorsBlock;
  let partnersBlock;
  let learnBlock;
  let emailInput;

  let isMobile = false;

  let stuckAt = maxTweenAmt;
  let nudgeDebounce = debounce(isStuck => {
    stuckAt = tweenAmt;
    anime({ targets: ".tween__nudge", easing: "easeInOutSine", opacity: 1 });
  }, 1000);

  $: if (!isMobile) isMobile = innerWidth < 1024;

  $: if (isMobile && scrollY > 0) {
    handleMouseScroll();
  }

  $: if (tweenAmt > stuckAt + 50) {
    stuckAt = maxTweenAmt;
    anime({ targets: ".tween__nudge", easing: "easeInOutSine", opacity: 0 });
  } else if (tweenAmt > 0 && tweenAmt < maxTweenAmt) {
    nudgeDebounce(true);
  } else {
    nudgeDebounce.cancel();
  }

  function connectLineTo(lineEl, startEl, endEl, startOffset, endOffset, overrides) {
    if (!startEl || !endEl || !lineEl) {
      return;
    }
    const startRect = startEl.getBoundingClientRect();
    const endRect = endEl.getBoundingClientRect();
    const startTop = startRect.top + scrollY;
    const endTop = endRect.top + scrollY;
    const [startX, startY] = startOffset;
    const [endX, endY] = endOffset;
    const shapeEl = document.querySelector(lineEl);
    if (!shapeEl) {
      return;
    }
    const shape = shapeEl.dataset.shape;
    const { width, left } = overrides || {};

    switch (shape) {
      case "TLBR":
        return anime.set(lineEl, {
          top: startTop + startY * startRect.height,
          left: left ? left : startRect.left + startX * startRect.width,
          height: endTop + endY * endRect.height - (startTop + startY * startRect.height),
          width: width ? width : endRect.left + endX * endRect.width - (startRect.left + startX * startRect.width)
        });
      case "TRBL":
        return anime.set(lineEl, {
          top: startTop + startY * startRect.height,
          left: left ? left : endRect.left + endX * endRect.width,
          height: endTop + endY * endRect.height - (startTop + startY * startRect.height),
          width: width ? width : startRect.left + startX * startRect.width - (endRect.left + endX * endRect.width)
        });
      case "BLTR":
        return anime.set(lineEl, {
          top: endTop + endY * endRect.height,
          left: left ? left : startRect.left + startX * startRect.width,
          height: startTop + startY * startRect.height - (endTop + endY * endRect.height),
          width: width ? width : endRect.left + endX * endRect.width - (startRect.left + startX * startRect.width)
        });
    }
  }

  function eagerLoadImages() {
    const imgList = document.querySelectorAll("img");
    for (let i = 0; i < imgList.length; i++) {
      imgList[i].setAttribute("loading", "eager");
    }
  }

  function adjustLines() {
    const isMobile = innerWidth < 1024;
    anime.set(".title", {
      translateX: isMobile ? "-18%" : "0%",
      translateY: isMobile ? "-18%" : "0%"
    });
    anime.set("#botHeroLine", {
      opacity: isMobile ? 0 : 1
    });
    anime.set("#botHeroLineM", {
      opacity: isMobile ? 1 : 0
    });
    connectLineTo("#topMidLine", topBlock, midBlock, [0.5, 1], [0.5, 0]);
    connectLineTo("#midBotLine", midBlock, botBlock, [0.5, 1], [0.5, 0]);
    if (isMobile) {
      connectLineTo("#botHeroLineM", botBlock, heroBlock, [0.4, 1], [0.555, 0]);
    } else {
      connectLineTo("#botHeroLine", botBlock, heroBlock, [1, 0.5], [-0.02, 0.5]);
    }
    connectLineTo("#heroSec1_1Line", heroBlock, section1_1Block, [isMobile ? 0.9 : 0.5, 1], [isMobile ? 0.1 : 0.45, 0]);
    connectLineTo(
      "#sec1_2Sec1_3Line",
      section1_2Block,
      section1_3Block,
      isMobile ? [Math.max((innerWidth - 200) / (1440 - 200), 0), 1] : [1, 0.5],
      [0.51, 0],
      { width: 63 }
    );
    connectLineTo(
      "#sec1_3Sec2_1Line",
      section1_3Block,
      section2_1Block,
      isMobile ? [Math.max(Math.min(innerWidth / 1440, 0.95), 0), 1] : [0.95, 0.9],
      [0, 0],
      { width: 51 }
    );
    connectLineTo(
      "#sec2_2Sec2_3Line",
      section2_2Block,
      section2_3Block,
      [isMobile ? 0.8 : 0.7, 1],
      [isMobile ? 0.2 : 0.5, -0.05]
    );
    connectLineTo(
      "#sec2_3Sec3_1Line",
      section2_3Block,
      section3_1Block,
      [isMobile ? 0.8 : 0.5, 1.1],
      isMobile ? [Math.max(Math.min(1, innerWidth / 1440), 0.6) - 0.4, 0] : [0.5, 0]
    );
    connectLineTo(
      "#sec3_2Sec3_3Line",
      section3_2Block,
      section3_3Block,
      isMobile ? [Math.max((innerWidth - 200) / (1440 - 200), 0), 1] : [1, 0.5],
      [0.51, 0],
      { width: 64 }
    );
    connectLineTo(
      "#sec3_3Sec4_1Line",
      section3_3Block,
      section4_1Block,
      isMobile ? [Math.max(Math.min(innerWidth / 1000, 0.95), 0), 1] : [1.02, 1.02],
      [0, 0],
      { width: 20 }
    );
    connectLineTo(
      "#sec4_2Sec4_3Line",
      section4_2Block,
      section4_3Block,
      [isMobile ? 0.5 : 0.7, 1],
      [isMobile ? 0.1 : 0.5, 0]
    );
    connectLineTo("#sec4_3Tween_0Line", section4_3Block, tween0Block, [0.7, 1], [0.5, isMobile ? 0 : -0.3]);

    if (!isMobile) {
      connectLineTo("#tween1_2", tween1Block, tween2Block, [0.5, 1], [0.5, 0]);
      connectLineTo("#tween2_3", tween2Block, tween3Block, [1, 0.5], [1, 0.5]);
      connectLineTo("#tween3_4", tween3Block, tween4Block, [0.5, 1], [0.5, 0]);
      connectLineTo("#tween5_0", tween5Block, tween0Block, [0.5, 1], [0.47, 0.1]);
      connectLineTo("#tween0_6", tween0Block, tween6Block, [0.7, 1], [0.5, 0]);

      connectLineTo("#tween0_6Investors", tween6Block, investorsBlock, [0.5, 1], [0.55, -0.2]);
      connectLineTo(
        "#partnersLearn",
        partnersBlock,
        learnBlock,
        isMobile ? [Math.max(Math.min(1, (innerWidth - 767) / (1440 - 767)), 0.2) - 0.1, 1.1] : [0.9, 0.5],
        [-0.05, 0],
        { width: 103 }
      );
    }
  }

  function lockBody(bool) {
    document.body.style.overflowY = bool ? "hidden" : "scroll";
  }

  function initLines() {
    anime({
      targets: ".title",
      translateX: isMobile ? "-18%" : "0%",
      translateY: isMobile ? "-18%" : "0%",
      duration: isMobile ? 1000 : 0,
      delay: 1000,
      easing: "easeInOutCirc"
    });
    anime({
      targets: ".path",
      strokeDashoffset: anime.setDashoffset,
      duration: 0
    });
    anime({
      targets: ["#tween1_2__path", "#tween2_3__path", "#tween3_4__path"],
      strokeDashoffset: el => el.getTotalLength() * 3,
      duration: 0
    });
    anime({
      targets: [".dot1", ".dot2"],
      strokeWidth: 0,
      duration: 0
    });
    anime({
      targets: [
        "section p",
        "section h3",
        ".section__one img",
        ".section__two img",
        ".section__three img",
        ".section__four img",
        ".investors img",
        "h4",
        ".investors__partners--item",
        ".learn h2",
        ".learn span",
        ".learn a",
        ".tween__logo"
      ],
      opacity: 0
    });
    anime({
      targets: ".tween__nudge",
      easing: "easeInOutSine",
      translateY: 25,
      direction: "alternate",
      loop: true
    });
  }

  function drawLine(elStr, delay = 0, duration = 1000) {
    anime({
      targets: `${elStr}__path`,
      strokeDashoffset: [anime.setDashoffset, 0],
      duration,
      delay,
      easing: "easeInOutCirc",
      begin: () => {
        anime({
          targets: `${elStr}__dot1`,
          strokeWidth: [0, 6],
          duration: 250,
          delay,
          easing: "linear"
        });
        anime({
          targets: `${elStr}__dot2`,
          strokeWidth: [0, 6],
          duration: 250,
          delay: delay + 700,
          easing: "linear"
        });
      }
    });
  }

  let animation1done = false;
  $: if (index === 1) {
    !animation1done && animation1();
  }
  let animation2done = false;
  $: if (index === 2) {
    !animation2done && animation2();
  }
  let animation3done = false;
  $: if (index === 3) {
    !animation3done && animation3();
  }
  let animation4done = false;
  $: if (index === 4 || index === 5) {
    !animation4done && animation4();
  }
  let animation6done = false;
  $: if (isMobile ? index === 6 : index === 6 && isBelowTween) {
    !animation6done && animation6();
  }

  function animation0() {
    adjustLines();
    anime
      .timeline({
        duration: 1000,
        easing: "easeInOutCirc"
      })
      .add({
        targets: ".intro__block",
        delay: anime.stagger(100),
        width: 0
      })
      .add(
        {
          targets: ".title__block",
          delay: anime.stagger(100),
          width: "100%"
        },
        "-=900"
      )
      .add(
        {
          targets: ".title__block",
          background: (el, i) => [["#aac4e5", "#fbab61", "#e0654f"][i], "#f9f9f9"],
          translateX: (el, i) => (i < 1 ? -24 : i > 1 ? 24 : 0),
          translateY: (el, i) => (i < 1 ? -24 : i > 1 ? 24 : 0),
          complete: () => {
            adjustLines();
          }
        },
        "-=250"
      )
      .add(
        {
          targets: ".title__block span",
          opacity: 1,
          duration: 500,
          delay: anime.stagger(500, { start: 200 }),
          begin: () => {
            drawLine("#topMidLine", 250);
            drawLine("#midBotLine", 750);
          }
        },
        "-=600"
      )
      .add(
        {
          targets: "nav",
          opacity: 1,
          duration: 500,
          translateY: ["-100%", "0%"]
        },
        1500
      )
      .add(
        {
          targets: ".waitlist",
          opacity: 1,
          duration: 500,
          translateY: ["-26px", "0%"]
        },
        1500
      )
      .add(
        {
          targets: ".title h1",
          opacity: 0,
          translateX: 25,
          complete: () => {
            lockBody(false);
            drawLine("#botHeroLine", 750);
            drawLine("#botHeroLineM", 750);
          }
        },
        1250
      )
      .add({
        targets: ".title h2",
        opacity: 1,
        delay: 0,
        begin: () => {
          drawLine("#heroSec1_1Line", 500);
        },
        complete: () => {
          eagerLoadImages();
          if (window.innerHeight > 1100 && scrollY === 0) {
            window.scrollTo(0, 1); // To trigger index = 1 on long pages
          }
        }
      });

    tweenAnimation = anime
      .timeline({
        duration: 1000,
        easing: "easeInOutCirc",
        autoplay: false
      })
      .add(
        {
          targets: ".tween .center-text h2:first-of-type",
          opacity: 1
        },
        0
      )
      .add(
        {
          targets: "#sec4_3Tween_0Line",
          opacity: 0
        },
        0
      )
      .add(
        {
          targets: ".tween img:not(:last-of-type)",
          opacity: 0,
          delay: anime.stagger(100)
        },
        0
      )
      .add(
        {
          targets: ".tween .center-text article p",
          opacity: [0, 1],
          delay: anime.stagger(250)
        },
        500
      )
      .add(
        {
          targets: ["#tween5_0__path", "#tween0_6__path"],
          strokeDashoffset: [anime.setDashoffset, 0],
          delay: anime.stagger(1000)
        },
        5000
      )
      .add(
        {
          targets: ["#tween5_0__dot1", "#tween0_6__dot1"],
          strokeWidth: [0, 6],
          duration: 250,
          delay: anime.stagger(1000),
          easing: "linear"
        },
        5000
      )
      .add(
        {
          targets: ["#tween5_0__dot2", "#tween0_6__dot2"],
          strokeWidth: [0, 6],
          duration: 250,
          delay: anime.stagger(1000, { start: 800 }),
          easing: "linear"
        },
        5000
      )
      .add(
        {
          targets: ".tween .center-text h2:first-of-type",
          opacity: [1, 0],
          translateX: ["-50%", "-50%"],
          translateY: ["-50%", "-50%"],
          scale: [1, 0.8]
        },
        2000
      )
      .add(
        {
          targets: ".tween .center-text h2:last-of-type",
          opacity: [0, 1],
          translateX: ["-50%", "-50%"],
          translateY: ["-50%", "-50%"],
          scale: [0.8, 1]
        },
        2000
      )
      .add(
        {
          targets: ["#tween1_2__path", "#tween2_3__path", "#tween3_4__path"],
          strokeDashoffset: [
            el => el.getTotalLength() * 3,
            el => el.getTotalLength() * 2,
            el => el.getTotalLength() * 1
          ],
          duration: 2000,
          delay: anime.stagger(1000)
        },
        1500
      )
      .add({
        targets: ["#tween0_6Investors__path"],
        strokeDashoffset: [anime.setDashoffset, 0],
        duration: 1000
      })
      .add(
        {
          targets: ["#tween0_6Investors__dot1"],
          strokeWidth: [0, 6],
          duration: 500,
          easing: "linear"
        },
        "-=1000"
      )
      .add(
        {
          targets: ["#tween0_6Investors__dot2"],
          strokeWidth: [0, 6],
          duration: 150,
          easing: "linear"
        },
        "-=500++"
      )
      .add(
        {
          targets: ["#tween1_2__dot1", "#tween2_3__dot1", "#tween3_4__dot1"],
          strokeWidth: [0, 6, 0],
          duration: 2000,
          delay: anime.stagger(500),
          easing: "linear"
        },
        1000
      )
      .add(
        {
          targets: ["#tween1_2__dot2", "#tween2_3__dot2", "#tween3_4__dot2"],
          strokeWidth: [0, 6, 0],
          duration: 2000,
          delay: anime.stagger(1000),
          easing: "linear"
        },
        1500
      )
      .add(
        {
          targets: [".center-text p:nth-child(1)", ".center-text p:nth-child(2)", ".center-text p:nth-child(3)"],
          opacity: [1, 0],
          delay: anime.stagger(1000)
        },
        2000
      )
      .add(
        {
          targets: ".tween .center-text h2:last-of-type",
          opacity: [1, 0],
          translateX: ["-50%", "-50%"],
          translateY: ["-50%", "-50%"],
          scale: [1, 0.8]
        },
        4500
      )
      .add(
        {
          targets: ".tween .center-text .tween__logo",
          opacity: [0, 1],
          translateX: ["-50%", "-50%"],
          translateY: ["-50%", "-50%"],
          scale: [0.9, 1]
        },
        4500
      )
      .add(
        {
          targets: ".tween img:last-of-type",
          opacity: [0, 1]
        },
        5500
      );
  }

  function animation1() {
    animation1done = true;
    drawLine("#sec1_2Sec1_3Line", 500);

    anime
      .timeline({
        duration: 1000,
        easing: "easeInOutCirc"
      })
      .add(
        {
          targets: ".section__one article p",
          opacity: 1,
          delay: anime.stagger(100)
        },
        0
      )
      .add(
        {
          targets: ".section__one img",
          opacity: 1,
          delay: anime.stagger(500)
        },
        500
      )
      .add(
        {
          targets: ".section__one h3",
          opacity: 1
        },
        600
      );
  }

  function animation2() {
    animation2done = true;
    drawLine("#sec1_3Sec2_1Line", 0);
    drawLine("#sec2_2Sec2_3Line", 1000);

    anime
      .timeline({
        duration: 1000,
        easing: "easeInOutCirc"
      })
      .add(
        {
          targets: ".section__two article p",
          opacity: 1,
          delay: anime.stagger(100)
        },
        500
      )
      .add(
        {
          targets: ".section__two img",
          opacity: 1,
          delay: anime.stagger(1000)
        },
        1000
      )
      .add(
        {
          targets: ".section__two h3",
          opacity: 1
        },
        500
      );
  }

  function animation3() {
    animation3done = true;
    drawLine("#sec2_3Sec3_1Line", 0);
    drawLine("#sec3_2Sec3_3Line", 1000);

    anime
      .timeline({
        duration: 1000,
        easing: "easeInOutCirc"
      })
      .add(
        {
          targets: ".section__three article p",
          opacity: 1,
          delay: anime.stagger(100)
        },
        500
      )
      .add(
        {
          targets: ".section__three img",
          opacity: 1,
          delay: anime.stagger(1000)
        },
        1000
      )
      .add(
        {
          targets: ".section__three h3",
          opacity: 1
        },
        1100
      );
  }

  function animation4() {
    animation4done = true;
    drawLine("#sec3_3Sec4_1Line", 0);
    drawLine("#sec4_2Sec4_3Line", 1000);

    anime
      .timeline({
        duration: 1000,
        easing: "easeInOutCirc"
      })
      .add(
        {
          targets: ".section__four article p",
          opacity: 1,
          delay: anime.stagger(100)
        },
        500
      )
      .add(
        {
          targets: ".section__four img",
          opacity: 1,
          delay: anime.stagger(1000)
        },
        1000
      )
      .add(
        {
          targets: ".section__four h3",
          opacity: 1,
          complete: () => {
            drawLine("#sec4_3Tween_0Line", 0);
          }
        },
        1100
      );
  }

  function animation5() {
    animation5done = true;

    anime
      .timeline({
        duration: 1000,
        easing: "easeInOutCirc"
      })
      .add(
        {
          targets: ".tween .center-text h2:first-of-type",
          opacity: 1
        },
        1000
      )
      .add(
        {
          targets: ".tween img:not(:last-of-type)",
          opacity: 1,
          delay: anime.stagger(250)
        },
        1100
      );
  }

  function animation6() {
    animation6done = true;

    anime
      .timeline({
        duration: 1000,
        easing: "easeInOutCirc"
      })
      .add({
        targets: ".investors h3:first-of-type",
        opacity: 1,
        duration: 50
      })
      .add({
        targets: ".investors__list img",
        opacity: 1,
        delay: anime.stagger(25)
      })
      .add({
        targets: ".investors h3:last-of-type",
        opacity: 1,
        duration: 50
      })
      .add({
        targets: ".investors__partners--item",
        opacity: 1,
        delay: anime.stagger(25),
        complete: () => {
          drawLine("#partnersLearn", 0);
        }
      })
      .add(
        {
          targets: [".learn h2", ".learn span", ".learn a", ".learn img"],
          opacity: 1,
          delay: anime.stagger(50)
        },
        "-=250"
      );
  }

  let animation7done = false;
  function animation7() {
    animation7done = true;

    anime
      .timeline({
        duration: 1000,
        easing: "easeInOutCirc"
      })
      .add(
        {
          targets: ".tween__mobile .center-text h2",
          opacity: [1, 0],
          translateX: ["-50%", "-50%"],
          translateY: ["-50%", "-50%"],
          scale: [1, 0.8]
        },
        0
      )
      .add(
        {
          targets: ".tween__mobile .center-text .tween__logo",
          opacity: [0, 1],
          translateX: ["-50%", "-50%"],
          translateY: ["-50%", "-50%"],
          scale: [0.9, 1]
        },
        500
      )
      .add(
        {
          targets: ".tween__mobile img",
          opacity: [0, 1]
        },
        750
      );
  }

  function showNav(bool) {
    if (navHidden && bool) {
      anime({
        targets: "header",
        easing: "easeInOutCirc",
        opacity: 1,
        duration: 500,
        translateY: "0%",
        delay: 150
      });
      navHidden = false;
    } else if (!navHidden && !bool) {
      anime({
        targets: "header",
        easing: "easeInOutCirc",
        opacity: 0,
        duration: 500,
        translateY: "-100%",
        delay: 150
      });
      navHidden = true;
    }
  }

  function handleMouseScroll(e) {
    if (isMobile) {
      if (!tweenBlockCenter) {
        return;
      }
      const clientRects = tweenBlockCenter.getClientRects()[0];
      if (!clientRects) {
        return;
      }
      const tweenOffset = Math.floor(tweenBlockCenter.getClientRects()[0].top);
      if (!animation7done) {
        offset1 = tweenOffset;
        if (tweenOffset - window.innerHeight <= 0) {
          animation7();
        }
      }
      return;
    }

    if (isBelowTween) {
      // Yes I meant to get rid of the reverse animation, Art ;)
      lockBody(false);
      return;
    }

    function incTween() {
      const tweenOffset = Math.floor(tweenBlock.getBoundingClientRect().top);
      tweenAnimation.seek(tweenAnimation.duration * (tweenAmt / maxTweenAmt));
      if (!isBelowTween) {
        if (tweenOffset <= 0 && tweenAmt <= maxTweenAmt && tweenAmt >= 0) {
          anime.remove(["#sec4_3Tween_0Line", ".tween .center-text h2:first-of-type", ".tween img:not(:last-of-type)"]);
          showNav(false);
          lockBody(true);
          window.scrollTo(0, window.scrollY + tweenOffset);
          tweenAmt += normalizeWheel(e).pixelY;
        } else if (tweenAmt > maxTweenAmt) {
          showNav(true);
          isBelowTween = true;
          tweenAmt = maxTweenAmt;
          lockBody(false);
        } else if (tweenAmt < 0) {
          showNav(true);
          tweenAmt = 0;
          lockBody(false);
        }
      } else {
        if (tweenOffset >= 0 && tweenAmt >= 0 && tweenAmt <= maxTweenAmt) {
          showNav(false);
          lockBody(true);
          window.scrollTo(0, window.scrollY + tweenOffset);
          tweenAmt += normalizeWheel(e).pixelY;
        } else if (tweenAmt < 0) {
          showNav(true);
          isBelowTween = false;
          tweenAmt = 0;
          lockBody(false);
        } else if (tweenAmt > maxTweenAmt) {
          showNav(true);
          tweenAmt = maxTweenAmt;
          lockBody(false);
        } else {
          showNav(true);
        }
      }
    }
    requestAnimationFrame(incTween);
  }

  function getUtmSource() {
    const url = new URL(window.location.href)
    const params = new URLSearchParams(url.search)
    const utmSource = params.get('utm_source')
    window.utmSource = utmSource
  }

  function track(name, opts) {
    if ('analytics' in window) {
      window.analytics.track(name, opts);
    }
  }

  function onScroll() {
    if (progress >= 1) {
      track('scrolled_to_bottom', {
        utm_source: window.utmSource
      })

      window.removeEventListener('scroll', onScroll)
    }
  }

  onMount(() => {
    lockBody(true);

    getUtmSource()

    window.addEventListener('scroll', onScroll)

    // helps prevent accidental triggers
    window.onbeforeunload = () => {
      anime.set("body", { opacity: 0 });
      window.scrollTo(0, 0);
    };

    window.onload = () => {
      if ('analytics' in window) {
        window.analytics.page();
      }
      setTimeout(() => {
        track('page_load', {
          utm_source: window.utmSource
        });
      }, 1000)
    }

    setTimeout(() => {
      initLines();
      animation0();
      window.scrollTo(0, 0);
    }, 250);

    fetch('https://api.runway.com/auth/check', { credentials: 'include' }).then(async (res) => {
      const loggedInResponse = await res.json();
      if (loggedInResponse.loggedIn) {
        window.location = 'https://app.runway.com/';
      }
    })
  });

  $: if (innerWidth || innerHeight) {
    requestAnimationFrame(adjustLines);
  }

  var typeformHidden = {};
  function handleWaitlistSubmit(ev) {
    ev.preventDefault();

    track('waitlist_submitted', {
      utm_source: window.utmSource
    })

    const formData = new FormData(ev.target)
    const email = formData.get('email');
    const inviteCode = formData.get('inviteCode');
    typeformHidden['email'] = email;
    typeformHidden['invite_code'] = inviteCode;

    fetch('https://api.runway.com/waitlist', {
      credentials: 'include',
      method: 'POST',
      body: JSON.stringify({email, inviteCode}),
    }).then(() => {
      anime.set(".step1", {display: 'none'})
      anime.set(".step2", {display: 'flex'})
   });
  }

  function openSurvey() {
    const { open } = typeform.createPopup("tre7lekO", {
      autoClose: 2000,
      size: 70,
      medium: 'snippet',
      onSubmit: submitTypeform,
      hidden: typeformHidden,
    })
    open();
  }

  function submitTypeform() {
    anime.set(".step2", {display: 'none'})
    anime.set(".step3", {display: 'flex'})
  }

  function jumpToSignup(ev) {
    ev.preventDefault();
    emailInput.focus();
    window.scrollTo(0, 0);
  }

  function handleWaitlistInputFocus() {
    track('waitlist_focused', {
      utm_source: window.utmSource
    })
  }

  function handleLinkClick(destination) {
    track('link_clicked', {
      destination,
      utm_source: window.utmSource
    })
  }

</script>

<style lang="scss">
  $white: #f9f9f9;
  $black: #1d2122;
  $colorPrimary: #aac4e5;
  $colorSecondary: #fbab61;
  $colorTertiary: #e0654f;

  body {
    overflow: hidden; /* Hide scrollbars */
  }

  main {
    width: 100%;
    max-width: 1440px;
    margin: 0 auto;
  }

  .emoji {
    font-size: 24px;
    position: relative;
    line-height: 14px;
    top: 3px;
  }

  .loader {
    display: flex;
    height: 100vh;
    max-height: 1100px;
    width: 100%;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    position: relative;
  }

  .intro {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    pointer-events: none;
    position: fixed;
    pointer-events: none;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 3;

    &__block {
      flex: 1;
      background-color: $white;
      width: 100%;
    }
  }

  .title {
    display: flex;
    align-items: center;
    position: relative;

    &__blocks {
      position: relative;
      display: flex;
      align-items: flex-end;
      flex-direction: column;
      width: 156px;
    }

    &__block {
      display: flex;
      position: relative;
      justify-content: center;
      align-items: center;
      height: 26px;
      width: 0px;
      margin-bottom: 24px;
      transform-origin: 100%;
      z-index: 0;

      span {
        color: $black;
        opacity: 0;
      }

      &:nth-child(1) {
        background-color: $colorPrimary;
      }
      &:nth-child(2) {
        background-color: $colorSecondary;
      }
      &:nth-child(3) {
        background-color: $colorTertiary;
      }
    }

    h1 {
      font-family: Suisse Intl;
      font-weight: 600;
      font-size: 174px;
      margin: 0;
      line-height: 0;
      margin: 0 13px;
      position: relative;
      top: -8px;

      @media screen and (max-width: 1024px) {
        display: none;
      }
    }

    h2 {
      opacity: 0;
      position: absolute;
      right: 0;
      top: 0;
      font-family: Suisse Intl;
      font-weight: bold;
      font-size: 81px;
      line-height: 1;
      white-space: pre-line;
      margin: 0;
      transform: translate(25%, -60%);

      @media screen and (max-width: 1140px) {
        transform: translate(5%, -60%);
      }

      @media screen and (max-width: 1024px) {
        position: relative;
        font-size: 48px;
        transform: translate(70px, 100px);
      }

      @media screen and (max-width: 414px) {
        font-size: 12vw;
      }
    }

    @media screen and (max-width: 1024px) {
      flex-direction: column;
    }
    @media screen and (max-width: 414px) {
      margin-top: 80px;
    }
  }

  .waitlist {
    font-family: Suisse Intl;
    position: relative;
    width: 100%;
    height: 80px;
    display: flex;
    justify-content: center;
    transform: translateY(-26px);
    opacity: 0;

    @media screen and (max-width: 1024px) {
      z-index: 2;
      height: 208px;
    }

    .container {
      position: absolute;
      top: calc(max(-220px, -21vh));
      box-shadow: 0px 4px 50px rgba(0, 0, 0, 0.25);
      border-radius: 12px;
      background-color: #3d3e3e;
      width: 720px;
      height: 100%;
      padding: 0 16px 0 28px;
      display: flex;

      @media screen and (max-width: 1024px) {
        width: 100%;
        top: -26px;
        padding-bottom: 16px;
      }

      .step {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
        @media screen and (max-width: 1024px) {
          align-items: flex-start;
          flex-direction: column;

          button {
            width: 100%;
          }
        }

      }

      .step2, .step3 {
        display: none;
        color: white;
        width: 100%;
        @media screen and (max-width: 1024px) {
          flex-direction: column;
          justify-content: space-around;
        }

        div {
          flex-shrink: 1;
          margin-right: 16px;
        }
        button {
          flex-grow: 1;
          padding: 0 16px;
          white-space: nowrap;
          @media screen and (max-width: 1024px) {
            flex-grow: 0;
          }
        }
      }
    }

    input {
      color: white;
      background-color: transparent;
      border: 0;
      height: 24px;
      min-width: 230px;
      margin: 0;
      flex-grow: 1;

      &:active, &:focus {
        outline: none;
      }
    }

    button {
      cursor: pointer;
      color: white;
      background-color: $colorTertiary;
      border-radius: 6px;
      width: 182px;
      border: 0;
      margin: 0;
      height: 48px;
    }
  }

  section {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0 120px;
    width: 100%;

    article {
      display: flex;
      flex-direction: column;
      align-items: flex-start;

      @media screen and (max-width: 414px) {
        align-items: stretch !important;
        width: 100%;
      }
    }

    p {
      background-color: $white;
      color: $black;
      line-height: 1.8;
      margin: 0 0 16px 0;
      padding: 8px 16px;

      &:last-child {
        margin: 0;
      }

      @media screen and (max-width: 1024px) {
        font-size: 14px;
      }
    }

    h2 {
      font-family: Suisse Intl;
      font-weight: bold;
      font-size: 81px;
      line-height: 1;
      letter-spacing: -0.025em;
      margin: 0;
      white-space: pre-line;
    }

    h3 {
      font-family: Suisse Intl;
      font-weight: bold;
      font-size: 28px;
      line-height: 1.2;
      margin: 0;
      white-space: pre-line;

      @media screen and (max-width: 1024px) {
        max-width: 400px;
      }
    }

    h4 {
      font-family: Suisse Intl;
      font-weight: bold;
      font-size: 24px;
      line-height: 1.2;
      margin: 0;
      white-space: pre-line;
    }

    @media screen and (max-width: 1024px) {
      padding: 0 24px;

      h2,
      h3,
      h4 {
        white-space: unset;
      }
    }
  }

  .section {
    &__one {
      article {
        align-self: flex-start;
        margin-left: 14.8%;
        margin-bottom: 355px;

        @media screen and (max-width: 1024px) {
          margin-left: 0%;
        }
      }

      img {
        align-self: flex-start;
        margin-bottom: 33px;
        width: 200px;
        height: 200px;

        @media screen and (max-width: 1024px) {
          position: relative;
          width: 112px;
          height: 112px;
          align-self: flex-end;
          left: unset;
          right: 40px;
          top: -390px;
        }
      }

      h3 {
        position: relative;
        margin-bottom: 153px;
      }
    }

    &__two {
      article {
        align-self: flex-end;
        align-items: flex-end;
        margin-bottom: 350px;

        @media screen and (max-width: 1024px) {
          margin-bottom: 180px;
        }
      }

      img {
        align-self: flex-end;
        margin-bottom: 90px;
        width: 200px;
        height: 200px;

        @media screen and (max-width: 1024px) {
          width: 112px;
          height: 112px;
          align-self: flex-start;
          left: 110px;
          position: relative;
          top: -50px;
          margin-bottom: 100px;
        }
      }

      h3 {
        margin-bottom: 65px;
      }
    }

    &__three {
      article {
        align-self: flex-start;
        margin-bottom: 210px;
        align-items: flex-start;
      }
      img {
        &:nth-of-type(1) {
          align-self: flex-end;
          margin-bottom: 50px;
          width: 304px;
          height: 131px;
        }
        &:nth-of-type(2) {
          align-self: flex-start;
          margin-bottom: 110px;
          width: 356px;
          height: 200px;
        }
      }
      h3 {
        margin-bottom: 44px;
      }
      @media screen and (max-width: 1024px) {
        article {
          margin-bottom: 0px;
        }

        h3 {
          margin-bottom: 60px;
        }

        img {
          &:nth-of-type(1) {
            align-self: flex-end;
            margin-bottom: 30px;
            margin-top: 245px;
            width: 162px;
            height: 70px;
          }
          &:nth-of-type(2) {
            align-self: flex-start;
            margin-bottom: 175px;
            width: 202px;
            height: 114px;
          }
        }
      }
    }

    &__four {
      article {
        align-self: flex-end;
        margin-bottom: 260px;
        align-items: flex-end;
      }

      img {
        &:nth-of-type(1) {
          width: 261px;
          height: 200px;
          align-self: flex-end;
          margin-bottom: -60px;

          @media screen and (max-width: 1024px) {
            width: 141px;
            height: 108px;
            margin-bottom: 30px;
          }
        }
        &:nth-of-type(2) {
          align-self: flex-start;
          width: 250px;
          height: 250px;
          object-fit: cover;
          object-position: 100% 0%;
          margin-bottom: 50px;
          height: 250px;
          transform: rotateY(180deg);

          @media screen and (max-width: 1024px) {
            width: 116px;
            height: 110px;
            margin-bottom: 100px;
          }
        }
      }

      h3 {
        margin-bottom: 95px;
      }

      @media screen and (max-width: 1024px) {
        article {
          margin-bottom: 0px;
        }

        h3 {
          margin-bottom: 35px;
        }

        img {
          &:nth-of-type(1) {
            align-self: flex-end;
            margin-bottom: 30px;
            margin-top: 190px;
            width: 141px;
            height: 108px;
          }
          &:nth-of-type(2) {
            align-self: flex-start;
            margin-bottom: 100px;
            width: 116px;
            height: 116px;
          }
        }
      }
    }
  }

  .tween {
    height: 100vh;
    max-height: 1080px;
    padding: 0;
    position: relative;
    justify-content: center;

    .center-text {
      display: flex;
      flex-direction: column;
      max-height: 800px;
      min-height: 800px;
      position: relative;
      height: 100%;
      width: 100%;

      img {
        &:nth-of-type(1) {
          position: absolute;
          width: 288px;
          height: 160px;
          top: 0;
          right: 120px;
          object-fit: cover;
          object-position: 50% 0%;
        }
        &:nth-of-type(2) {
          position: absolute;
          top: 130px;
          left: 120px;
          width: 200px;
          height: 132px;
        }
        &:nth-of-type(3) {
          position: absolute;
          margin-left: 50%;
          width: 255px;
          height: 144px;
          bottom: 80px;
          transform: translateX(calc(-50% - 100px));
        }
        &:nth-of-type(4) {
          position: absolute;
          width: 258px;
          height: 172px;
          left: 192px;
          top: 80px;
        }
      }

      h2 {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        white-space: pre;
      }

      article {
        p {
          position: absolute;
          white-space: nowrap;

          &:nth-of-type(1) {
            top: 112px;
            left: 77px;
          }
          &:nth-of-type(2) {
            bottom: 112px;
            left: 48px;
          }
          &:nth-of-type(3) {
            top: 50px;
            right: 142px;
          }
          &:nth-of-type(4) {
            top: 50%;
            right: 460px;
            transform: translateY(calc(-50% - 180px));
          }
          &:nth-of-type(5) {
            top: 50%;
            right: 356px;
            transform: translateY(calc(-50% - 130px));
          }
          &:nth-of-type(6) {
            top: 50%;
            transform: translateY(calc(-50% + 100px));
            right: 128px;
          }
        }
      }
    }

    &__logo {
      width: 437px;
      height: 96px;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    &__mobile {
      display: none;
    }

    &__nudge {
      position: absolute;
      font-size: 25px;
      right: 50px;
      bottom: 100px;
      opacity: 0;
    }

    @media screen and (max-width: 1024px) {
      &__mobile {
        display: flex;

        height: unset;
        max-height: unset;
        padding: 0 30px;
        justify-content: center;
        align-items: center;

        .center-text {
          max-height: unset;
          min-height: unset;
          height: 60px;
          width: unset;
          margin-bottom: 210px;
        }

        img {
          position: relative !important;
          width: 217px !important;
          height: 145px !important;
          top: 100px !important;
          right: unset !important;
          left: -60px !important;
        }

        .tween__logo {
          width: 236px !important;
          height: unset !important;
        }

        h3 {
          margin-top: 400px;
          font-size: 48px;
        }

        h2 {
          font-size: 48px;
        }

        h3,
        p {
          opacity: 1 !important;
          width: 100%;
          margin: 0;
          max-width: 400px;
        }
      }
    }
  }

  .investors {
    padding: 0px 120px 100px;
    align-items: flex-start;
    width: 100%;

    h3 {
      line-height: 1.1;
      margin-bottom: 50px;
    }

    &__list {
      display: flex;
      width: 100%;
      margin-bottom: 120px;
      min-height: 70px;

      a {
        display: inline-block;
        transform-style: preserve-3d;
        transform: scale(1);
        transition: transform 0.2s ease-in-out;

        &:not(:last-child) {
          margin-right: 10%;
        }

        &:hover {
          transform: scale(1.04);
        }
      }

      @media screen and (max-width: 1024px) {
        flex-direction: column;
        margin-bottom: 90px;

        a {
          margin-bottom: 50px;
        }
      }
    }
    &__partners {
      display: grid;
      grid-template-columns: auto auto;
      grid-column-gap: 90px;
      grid-row-gap: 90px;

      a {
        &:not(:last-child):after {
          content: ",";
          color: $white;
        }
      }

      &--item {
        div {
          line-height: 1.6;

          &:nth-child(1) {
            font-size: 24px;
            line-height: 1.4;
            letter-spacing: -0.025em;
          }
        }
      }
      @media screen and (max-width: 1024px) {
        display: flex;
        flex-direction: column;

        &--item {
          margin-bottom: 55px;
        }
      }
    }

    @media screen and (max-width: 1024px) {
      padding: 0 30px;
      margin-top: 20px;
    }
  }

  .learn {
    padding: 0px 120px 350px;
    align-items: flex-end;
    position: relative;

    h2 {
      font-size: 81px;
      margin-bottom: 40px;
    }
    span {
      display: block;
      white-space: pre;
      margin-bottom: 20px;
      line-height: 28px;
    }
    a {
      display: inline-flex;
      white-space: nowrap;

      span {
        transform-style: preserve-3d;
        transform: translate(0px, -2px);
        transition: transform 0.2s ease-in-out;
      }

      &:hover {
        text-decoration: none;

        span {
          transform: translate(6px, -2px);
        }
      }
    }
    img {
      width: 382px;
      height: 255px;
      position: absolute;
      left: 170px;
      top: 320px;
    }

    @media screen and (max-width: 1024px) {
      padding: 0px 30px 30px;
      align-items: flex-start;

      h2 {
        font-size: 48px;
        margin-bottom: 40px;
      }
      span {
        margin-bottom: 16px;
        white-space: unset;
        font-size: 16px;
        .emoji {
          font-size: 24px;
          line-height: 16px;
          top: 3px;
          position: relative;
        }
      }

      img {
        width: 231px;
        height: 154px;
        position: relative;
        align-self: center;
        left: unset;
        top: unset;
        margin: 80px auto 0;
        display: block;
      }
    }
  }

  .hidden-mobile {
    @media screen and (max-width: 1024px) {
      display: none;
    }
  }

  .visible-mobile {
    display: none;
    @media screen and (max-width: 1024px) {
      display: unset;
    }
  }

  header {
    display: flex;
    position: fixed;
    align-items: center;
    width: 100vw;
    height: 96px;
    z-index: 2;
    pointer-events: none;
    nav {
      font-family: Suisse Intl;
      font-size: 18px;
      display: flex;
      justify-content: flex-start;
      padding: 0 120px;
      max-width: 1440px;
      width: 100%;
      transform: translateY(-100%);
      opacity: 0;

      .spacer {
        display: flex;
        flex: 1 0;
        position: relative;
      }

      a {
        pointer-events: auto;
        color: white;
        &:visited {
          color: white;
        }
        &:first-child {
          width: 160px;
        }
        &:not(:first-child) {
          margin-left: 24px;
          position: relative;
          top: 12px;
        }
      }

      @media screen and (max-width: 1024px) {
        padding: 0 8px;

        a {
          &:first-child {
            height: 36px;
            width: 126px;
          }
          &:not(:first-child) {
            position: relative;
            top: 10px;
          }
        }
      }
    }

    div {
      position: absolute;
      top: 30px;
      left: 30px;
      p {
        font-size: 12px;
      }
    }
    @media screen and (max-width: 1024px) {
      background-color: $black;
    }
  }

  footer {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    padding: 50px;

    .links {
      display: flex;
      align-items: center;

      a {
        margin: 16px;
      }

      svg {
        transform: translateY(2px);
      }
    }

    .copyright {
      position: relative;
      transform: translateX(15px);

      &:before {
        content: "©";
        position: absolute;
        left: -18px;
      }
    }

    @media screen and (max-width: 1024px) {
      padding: 50px 18px;
      font-size: 14px;

      .links {
        flex-direction: column;

        &__dot {
          display: none;
        }

        a {
          margin: 8px;
        }

        svg {
          transform: translateY(1px);
        }
      }

      .copyright {
        margin-top: 12px;
        transform: translateX(0);
      }
    }
  }
</style>

<svelte:head>
  <link rel="stylesheet" href="/build/combined.css" />

  <!-- Google Tag Manager -->
<script>
  (
    function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl…
    })
    (window,document,'script','dataLayer','GTM-TJFF37D');
  </script>
  <!-- End Google Tag Manager -->

  <!-- Google Tag Manager (noscript) -->
  <noscript><iframe src="https://www.googletagmanager.com/ns.html?id=GTM-TJ…"
  height="0" width="0" style="display:none;visibility:hidden"></iframe></noscript>
  <!-- End Google Tag Manager (noscript) -->

  <script type="text/javascript">
    (function(){
      // Create a queue, but don't obliterate an existing one!
      var analytics = window.analytics = window.analytics || [];
      // If the real analytics.js is already on the page return.
      if (analytics.initialize) return;
      // If the snippet was invoked already show an error.
      if (analytics.invoked) {
        if (window.console && console.error) {
          console.error('Segment snippet included twice.');
        }
        return;
      }
      // Invoked flag, to make sure the snippet
      // is never invoked twice.
      analytics.invoked = true;
      // A list of the methods in Analytics.js to stub.
      analytics.methods = [
        'trackSubmit',
        'trackClick',
        'trackLink',
        'trackForm',
        'pageview',
        'identify',
        'reset',
        'group',
        'track',
        'ready',
        'alias',
        'debug',
        'page',
        'once',
        'off',
        'on',
        'addSourceMiddleware',
        'addIntegrationMiddleware',
        'setAnonymousId',
        'addDestinationMiddleware'
      ];
      // Define a factory to create stubs. These are placeholders
      // for methods in Analytics.js so that you never have to wait
      // for it to load to actually record data. The `method` is
      // stored as the first argument, so we can replay the data.
      analytics.factory = function(method){
        return function(){
          var args = Array.prototype.slice.call(arguments);
          args.unshift(method);
          analytics.push(args);
          return analytics;
        };
      };
      // For each of our methods, generate a queueing stub.
      for (var i = 0; i < analytics.methods.length; i++) {
        var key = analytics.methods[i];
        analytics[key] = analytics.factory(key);
      }
      // Define a method to load Analytics.js from our CDN,
      // and that will be sure to only ever load it once.
      analytics.load = function(key, options){
        // Create an async script element based on your key.
        var script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.src = 'https://app.runway.com/seg/cdn/analytics.js/v1/'
            + key + '/analytics.min.js';
        // Insert our script next to the first script element.
        var first = document.getElementsByTagName('script')[0];
        first.parentNode.insertBefore(script, first);
        analytics._loadOptions = options;
      };
      analytics._writeKey = 'YOUR_WRITE_KEY'
      // Add a version to keep track of what's in the wild.
      analytics.SNIPPET_VERSION = '4.15.2';
      // Load Analytics.js with your key, which will automatically
      // load the tools you've enabled for your account. Boosh!
      analytics.load("uGLqhYnDEJgLYLsWVlACp89JGmd94TY0");
      // Make the first page call to load the integrations. If
      // you'd like to manually name or tag the page, edit or
      // move this call however you'd like.
      analytics.page();
    })();
    </script>


</svelte:head>

<svelte:window bind:innerWidth bind:innerHeight bind:scrollY on:wheel={handleMouseScroll} />

<Scroller top={0} bottom={1} threshold={0.9} bind:index bind:progress>
  <div slot="background">
    <!-- <p>index: {index}</p>
    <p>tweenAmt: {tweenAmt}</p>
    <p>isMobile: {isMobile}</p>  -->
    <!-- <p>
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      isMobile: {scrollY} offset: {offset1}
    </p> -->
  </div>

  <main slot="foreground">
    <div style="height: 0">
      {#if !isMobile}
        <LineTLBR1
          id="tween1_2"
          path="M74.00 2.00 C 74.00 2.00, 242.00 93.00, 230.00 169.00 C 218.00 245.00, 1.00 179.00, 2.00 295.00 C 3.00
          411.00, 210.00 486.00, 210.00 486.00" />
        <LineBLTR1
          id="tween2_3"
          path="M2.00 590.00 C 2.00 590.00, 913.04 600.17, 546.00 338.00 C 476.00 288.00, 384.00 152.00, 505.00 152.00 C
          617.00 152.00, 707.00 55.00, 516.00 2.00" />
        <LineTRBL1
          id="tween3_4"
          path="M201.00 3.00 C 201.00 3.00, 241.85 42.75, 196.00 60.00 C 150.15 77.25, 51.22 50.22, 18.00 61.00 C -15.22
          71.78, 18.00 107.00, 18.00 107.00" />
        <LineTRBL1
          id="tween5_0"
          path="M208.00 2.00 C 208.00 2.00, 236.85 34.75, 191.00 52.00 C 145.15 69.25, 50.22 27.22, 17.00 38.00 C -16.22
          48.78, 17.00 80.00, 17.00 80.00" />
        <LineTLBR1
          id="tween0_6"
          path="M2.00 57.00 C 38.00 84.00, 140.00 152.00, 231.00 41.00 C 322.00 -70.00, 384.00 92.00, 384.00 92.00" />
        <LineTRBL1
          id="tween0_6Investors"
          path="M873.00 2.00 C 873.00 2.00, 842.00 148.00, 684.00 212.00 C 526.00 276.00, 284.00 207.00, 146.00 258.00 C
          8.00 309.00, 2.00 404.00, 2.00 404.00" />
        <LineTLBR1
          id="partnersLearn"
          path="M10.00 2.00 C 10.00 2.00, 196.00 63.00, 196.00 143.00 C 196.00 223.00, 36.00 256.00, 5.00 307.00 C
          -26.00 358.00, 174.00 386.00, 174.00 386.00" />
        <LineTRBL1
          id="sec4_3Tween_0Line"
          path="M235.00 2.00 C 235.00 2.00, 416.00 145.00, 416.00 314.00 C 416.00 483.00, 152.00 454.00, 39.00 544.00 C
          -74.00 634.00, 110.00 823.00, 110.00 823.00" />
      {/if}
      <!-- normal lines -->
      <LineTLBR1 id="topMidLine" />
      <LineTLBR1 id="midBotLine" />
      <LineBLTR1 id="botHeroLine" />
      <LineTLBR1
        id="botHeroLineM"
        path="M14.20 2.00 C 14.20 2.00, -12.20 60.88, 14.20 80.50 C 40.60 100.12, 93.40 60.88, 119.80 80.50 C 146.20
        100.12, 119.80 159.00, 119.80 159.00" />
      <LineTRBL1 id="heroSec1_1Line" />
      <LineTLBR1
        id="sec1_2Sec1_3Line"
        path="M2.00 2.00 C 2.00 2.00, 242.00 64.00, 242.00 145.00 C 242.00 226.00, 94.00 145.00, 29.00 226.00 C -36.00
        307.00, 71.00 364.00, 71.00 364.00" />
      <LineTLBR1
        id="sec1_3Sec2_1Line"
        path="M10.00 2.00 C 10.00 2.00, 233.00 89.00, 233.00 169.00 C 233.00 249.00, 58.00 205.00, 14.00 252.00 C -30.00
        299.00, 67.00 386.00, 67.00 386.00" />
      <LineTRBL1 id="sec2_2Sec2_3Line" />
      <LineTRBL1 id="sec2_3Sec3_1Line" />
      <LineTLBR1
        id="sec3_2Sec3_3Line"
        path="M2.00 2.00 C 2.00 2.00, 243.00 62.00, 243.00 148.00 C 243.00 234.00, 72.00 148.00, 23.00 233.00 C -26.00
        318.00, 72.00 364.00, 72.00 364.00" />
      <LineTLBR1
        id="sec3_3Sec4_1Line"
        path="M15.00 2.00 C 15.00 2.00, 238.00 75.00, 238.00 155.00 C 238.00 235.00, 61.00 185.00, 17.00 232.00 C -27.00
        279.00, 46.00 353.00, 46.00 353.00" />
      <LineTRBL1
        id="sec4_2Sec4_3Line"
        path="M466.00 2.00 C 466.00 2.00, 578.00 141.00, 474.00 196.00 C 370.00 251.00, 69.00 151.00, 17.00 205.00 C
        -35.00 259.00, 71.00 392.00, 71.00 392.00" />
      <LineTRBL1
        id="sec4_3Tween_0LineM"
        path="M235.00 2.00 C 235.00 2.00, 416.00 145.00, 416.00 314.00 C 416.00 483.00, 152.00 454.00, 39.00 544.00 C
        -74.00 634.00, 110.00 823.00, 110.00 823.00" />
    </div>
    <header>
      <nav>
        <a href="/">
          <Logo />
        </a>
        <div class="spacer" />
        <a
          on:click={() => handleLinkClick("https://runwayhq.notion.site/Working-at-Runway-c7a9a7c8ef91471aacc376b0a5cdb913")}
          href="https://runwayhq.notion.site/Working-at-Runway-c7a9a7c8ef91471aacc376b0a5cdb913"
        >We're hiring</a>
        <a
          on:click={() => handleLinkClick("https://twitter.com/runwayco")}
          class="hidden-mobile"
          href="https://twitter.com/runwayco"
        >Follow <strong>@runwayco</strong> for invites</a>
        <a
          on:click={() => handleLinkClick("https://app.runway.com")}
          class="hidden-mobile"
          href="https://app.runway.com"
        >Log in</a>
      </nav>
    </header>
    <section class="loader">
      <div class="waitlist">
        <div class="container">
          <form class="step step1" on:submit={handleWaitlistSubmit}>
            <input bind:this={emailInput} on:focus={handleWaitlistInputFocus} name="email" type="email" placeholder="Work email" required />
            <input name="inviteCode" type="text" placeholder="Invite code (optional)" />
            <button type="submit">Request access</button>
          </form>

          <div class="step step2">
            <div>
              Thanks for requesting access! For a chance to jump the line, answer
              just a few questions about your business.
            </div>
              <button id="typeform-button" on:click={openSurvey} >Begin survey</button>
          </div>
          <div class="step step3">
            <div>
              Thanks! Keep an eye on our Twitter for more opportunities to get Runway sooner.
            </div>
            <button
              on:click={() => {
                window.location = "https://twitter.com/runwayco"
                handleLinkClick("https://twitter.com/runwayco")
              }}
            >Follow @runwayco</button>
          </div>
        </div>
      </div>
      <div class="title">
        <div class="title__blocks">
          <div bind:this={topBlock} class="title__block title__block">
            <span>What</span>
          </div>
          <div bind:this={midBlock} class="title__block title__block">
            <span>is</span>
          </div>
          <div bind:this={botBlock} class="title__block title__block">
            <span>Runway?</span>
          </div>
        </div>
        <h1 class="hero__logo">Runway</h1>
        <h2 bind:this={heroBlock} class="hero__text">{`Understand\n your business`}</h2>
      </div>

      <div class="intro">
        {#each [0, 1, 2] as n (n)}
          <div class="intro__block" />
        {/each}
      </div>
    </section>
    <section class="section__one">
      <article>
        <p bind:this={section1_1Block}>
          It's the year {theYear}, why is understanding my burn so hard?
          <span class="emoji">🤔</span>
        </p>
        <p>Why do I feel I need a finance degree to read an income statement?</p>
        <p bind:this={section1_2Block}>
          Am I just bad at business?
          <span class="emoji">😟</span>
        </p>
      </article>
      <h3 bind:this={section1_3Block}>{`Why don't my financials match \nmy mental model of the business?`}</h3>
      <img loading="lazy" src="images/Huh.gif" alt="Huh?" />
    </section>
    <section class="section__two">
      <article>
        <p bind:this={section2_1Block}>
          Where are we investing?
          <span class="emoji">🧐</span>
        </p>
        <p>Where should we be investing instead?</p>
        <p bind:this={section2_2Block}>
          How can we grow faster?
          <span class="emoji">😎</span>
        </p>
      </article>
      <h3 bind:this={section2_3Block}>{`We need to act like owners, \nso why aren't we doing that?`}</h3>
      <img loading="lazy" src="images/JustDoIt.gif" alt="Just do it!" />
    </section>
    <section class="section__three">
      <article>
        <p bind:this={section3_1Block}>
          Who bought the 100th SaaS service that nobody uses?
          <span class="emoji">🤫</span>
        </p>
        <p>Who do I talk to about this invoice that came out of nowhere?</p>
        <p bind:this={section3_2Block}>
          Wait, why do we want to hire 10 more engineers?
          <span class="emoji">😶</span>
        </p>
      </article>
      <img loading="lazy" src="images/Team_1.gif" alt="High five!" />
      <h3 bind:this={section3_3Block}>{`How can we get on the \nsame page as a team?`}</h3>
      <img loading="lazy" src="images/Team_2.gif" alt="Go team!" />
    </section>
    <section class="section__four">
      <article>
        <p bind:this={section4_1Block}>Why are we still budgeting by emailing spreadsheets back and forth?</p>
        <p>
          Why does our finance team think we’re setting money on fire all day?
          <span class="emoji">🤑</span>
        </p>
        <p bind:this={section4_2Block}>Why does our finance team seem like a bunch of penny pinching paper pushers?</p>
      </article>
      <img loading="lazy" src="images/Painful_1.gif" alt="How painful..." />
      <h3 bind:this={section4_3Block}>{`How have we gotten so used \nto how painful all of this is?`}</h3>
      <img loading="lazy" src="images/Painful_2.gif" alt="So Dissapointing" />
    </section>
    {#if !isMobile}
      <section bind:this={tweenBlock} class="tween">
        <div class="center-text">
          <img loading="lazy" src="images/Why_not_1.gif" alt="Let me think..." />
          <img loading="lazy" src="images/Why_not_2.gif" alt="Just Let Me Think!" />
          <img loading="lazy" src="images/Look.gif" alt="Dear Mother of God..." />
          <img loading="lazy" src="images/Mind_blown.gif" alt="Mind Blown" />
          <h2>{`Why haven't we built \nsomething better?`}</h2>
          <h2>{`That's`}</h2>
          <div bind:this={tween0Block} class="tween__logo">
            <Logo />
          </div>

          <article>
            <p bind:this={tween1Block}>Something designed for human beings.</p>
            <p bind:this={tween2Block}>Something that fits right in with the amazing products we use today.</p>
            <p bind:this={tween3Block}>
              Something clear, powerful, social, and above all
              <strong>fast.</strong>
            </p>
            <p bind:this={tween4Block}>Something that fundamentally</p>
            <p bind:this={tween5Block}>rethinks the role of financial data...</p>
            <p bind:this={tween6Block}>for you and your team.</p>
          </article>
        </div>
        <div class="tween__nudge">👇</div>
      </section>
    {:else}
      <section class="tween tween__mobile">
        <h3 bind:this={tween0Block}>{`Why haven't we built \nsomething better?`}</h3>
        <StaticLine
          id="static-line-1"
          path="M122.11 2.00 C 122.11 2.00, 321.27 208.99, 252.00 264.00 C 182.73 319.01, 79.12 259.75, 18.00 328.00 C
          -43.12 396.25, 93.59 546.00, 93.59 546.00" />
        <p>Something designed for human beings.</p>
        <StaticLine id="static-line-2" />
        <p>Something that fits right in with the amazing products we use today.</p>
        <StaticLine id="static-line-3" />
        <p>
          Something clear, powerful, social, and above all
          <strong>fast.</strong>
        </p>
        <StaticLine id="static-line-4" />
        <p>Something that fundamentally rethinks the role of financial data for you and your team.</p>
        <StaticLine id="static-line-5" />
        <div bind:this={tweenBlockCenter} class="center-text">
          <img loading="lazy" src="images/Mind_blown.gif" alt="Mind Blown" />
          <h2>That's</h2>
          <div class="tween__logo">
            <Logo />
          </div>
        </div>
        <StaticLine id="static-line-6" />
      </section>
    {/if}
    <section class="investors">
      <h3 bind:this={investorsBlock}>{`Supported by a select group \nof people we admire including`}</h3>
      <div class="investors__list">
        <a target="__blank" href="https://a16z.com/" on:click={() => handleLinkClick("https://a16z.com/")}>
          <img loading="lazy" src="images/investor_1.svg" alt="Andreessen Horowitz" />
        </a>
        <a target="__blank" href="https://lsvp.com" on:click={() => handleLinkClick("https://lsvp.com")}>
          <img loading="lazy" src="images/investor_2.svg" alt="Lightspeed Venture Partners" />
        </a>
        <a target="__blank" href="https://combine.vc" style="position: relative; top: -6px;" on:click={() => handleLinkClick("https://combine.vc")}>
          <img loading="lazy" src="images/investor_3.svg" alt="Combine VC" />
        </a>
      </div>
      <h3>{`Individual investors and partners`}</h3>
      <div bind:this={partnersBlock} class="investors__partners">
        <div class="investors__partners--item">
          <div>Elad Gil</div>
          <div>Investor & Advisor</div>
          <div>
            <a target="__blank" href="https://airbnb.com" on:click={() => handleLinkClick("https://airbnb.com")}>Airbnb</a>
            <a target="__blank" href="https://airtable.com" on:click={() => handleLinkClick("https://airtable.com")}>Airtable</a>
            <a target="__blank" href="https://stripe.com" on:click={() => handleLinkClick("https://stripe.com")}>Stripe</a>
          </div>
        </div>
        <div class="investors__partners--item">
          <div>Naval Ravikant</div>
          <div>Investor & Advisor</div>
          <div>
            <a target="__blank" href="https://uber.com" on:click={() => handleLinkClick("https://uber.com")}>Uber</a>
            <a target="__blank" href="https://foursquare.com" on:click={() => handleLinkClick("https://foursquare.com")}>Foursquare</a>
            <a target="__blank" href="https://twitter.com" on:click={() => handleLinkClick("https://twitter.com")}>Twitter</a>
          </div>
        </div>
        <div class="investors__partners--item">
          <div>Dylan Field</div>
          <div>Co-founder & CEO</div>
          <div>
            <a target="__blank" href="https://figma.com" on:click={() => handleLinkClick("https://figma.com")}>Figma</a>
          </div>
        </div>
        <div class="investors__partners--item">
          <div>Henry Ward</div>
          <div>Co-founder & CEO</div>
          <div>
            <a target="__blank" href="https://carta.com" on:click={() => handleLinkClick("https://carta.com")}>Carta</a>
          </div>
        </div>
      </div>
    </section>
    <section class="learn">
      <div>
        <h2 bind:this={learnBlock}>{`Want to \nlearn more?`}</h2>
        <span>Tell us how you feel about finance. We won't tell 🤐</span>
        <a href="#" on:click={jumpToSignup}>
          Sign up
          <span>➞</span>
        </a>
        <img loading="lazy" src="/images/Yes.gif" alt="Huzzah" />
      </div>
    </section>

    <footer>
      <div class="links">
        <!-- <a
          rel="nofollow"
          target="__blank"
          href="https://www.notion.so/Open-Roles-at-Runway-bf1b7107285c4aa9adb42da50cdd2055">
          Join Our Team
        </a> -->
        <a target="__blank" rel="nofollow" href="https://runway.com/terms.pdf" on:click={() => handleLinkClick("https://runway.com/terms.pdf")}>Terms of Service</a>
        <span class="links__dot">•</span>
        <a rel="nofollow" target="__blank" href="https://twitter.com/RunwayCo" on:click={() => handleLinkClick("https://twitter.com/RunwayCo")}>
          <svg viewBox="0 0 32 32" height="24" width="24">
            <path
              fill="#AAC4E5"
              d="M32 7.075c-1.175 0.525-2.444 0.875-3.769 1.031 1.356-0.813 2.394-2.1 2.887-3.631-1.269 0.75-2.675
              1.3-4.169 1.594-1.2-1.275-2.906-2.069-4.794-2.069-3.625 0-6.563 2.938-6.563 6.563 0 0.512 0.056 1.012
              0.169 1.494-5.456-0.275-10.294-2.888-13.531-6.862-0.563 0.969-0.887 2.1-0.887 3.3 0 2.275 1.156 4.287
              2.919 5.463-1.075-0.031-2.087-0.331-2.975-0.819 0 0.025 0 0.056 0 0.081 0 3.181 2.263 5.838 5.269
              6.437-0.55 0.15-1.131 0.231-1.731 0.231-0.425 0-0.831-0.044-1.237-0.119 0.838 2.606 3.263 4.506 6.131
              4.563-2.25 1.762-5.075 2.813-8.156 2.813-0.531 0-1.050-0.031-1.569-0.094 2.913 1.869 6.362 2.95 10.069
              2.95 12.075 0 18.681-10.006 18.681-18.681 0-0.287-0.006-0.569-0.019-0.85 1.281-0.919 2.394-2.075
              3.275-3.394z" />
          </svg>
        </a>
        <span class="links__dot">•</span>
        <a target="__blank" rel="nofollow" href="https://runway.com/privacy.pdf" on:click={() => handleLinkClick("https://runway.com/privacy.pdf")}>Privacy Policy</a>
      </div>
      <div class="copyright">{new Date().getFullYear()}</div>
    </footer>
  </main>
</Scroller>
